import type JSZip from 'jszip'
import { allSteps } from './project'
import {
  createProject,
  downloadStorageBlob,
  listPresentationEditions,
  listProjectAttachments,
  listProjectAuditEvents,
  listProjectExports,
  listProjectImports,
  listProjectVersions,
  uploadProjectAttachment,
} from './repository'
import type {
  AuditEventRow,
  ExportRow,
  ImportRow,
  PresentationEditionRow,
  ProjectAttachment,
  ProjectRow,
  ProjectVersionRow,
} from '../types'

export const PROJECT_ARCHIVE_SCHEMA = 'suiviaziz-project-archive-v1'

type ArchiveEntry = {
  path: string
  sha256: string
  bytes: number
}

export type ProjectArchiveManifest = {
  schema: typeof PROJECT_ARCHIVE_SCHEMA
  created_at: string
  project: {
    id: string
    code: string
    name: string
    version: number
    as_of_date: string
  }
  entries: ArchiveEntry[]
}

export type ProjectArchiveData = {
  project: ProjectRow
  versions: ProjectVersionRow[]
  attachments: ProjectAttachment[]
  imports: ImportRow[]
  exports: ExportRow[]
  auditEvents: AuditEventRow[]
  presentations: PresentationEditionRow[]
}

export type ProjectArchiveFile = {
  path: string
  blob: Blob
}

export type LoadedProjectArchive = {
  manifest: ProjectArchiveManifest
  data: ProjectArchiveData
  files: Map<string, Blob>
}

function safeName(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'fichier'
}

function jsonBlob(value: unknown) {
  return new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
}

async function loadZipLibrary() {
  const module = await import('jszip')
  return module.default
}

async function sha256(blob: Blob) {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

function readJson<T>(text: string, label: string): T {
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(`${label} est illisible.`)
  }
}

function assertProjectArchiveData(value: ProjectArchiveData) {
  if (!value.project || !value.project.id || !value.project.code || !value.project.name || !value.project.snapshot) {
    throw new Error('Le contenu du projet archivé est incomplet.')
  }
  if (!Array.isArray(value.versions) || !Array.isArray(value.attachments) || !Array.isArray(value.imports) || !Array.isArray(value.exports) || !Array.isArray(value.auditEvents) || !Array.isArray(value.presentations)) {
    throw new Error('La structure de l’archive est invalide.')
  }
}

export async function packageProjectArchive(
  data: ProjectArchiveData,
  files: ProjectArchiveFile[],
): Promise<{ blob: Blob; manifest: ProjectArchiveManifest }> {
  assertProjectArchiveData(data)
  const JSZip = await loadZipLibrary()
  const zip = new JSZip()
  const entries: ArchiveEntry[] = []

  async function add(path: string, blob: Blob) {
    const normalised = path.replace(/^\/+/, '')
    if (!normalised || zip.file(normalised)) throw new Error(`Entrée d’archive invalide : ${path}`)
    zip.file(normalised, blob)
    entries.push({ path: normalised, sha256: await sha256(blob), bytes: blob.size })
  }

  await add('project/project.json', jsonBlob(data.project))
  await add('project/versions.json', jsonBlob(data.versions))
  await add('project/attachments.json', jsonBlob(data.attachments))
  await add('project/imports.json', jsonBlob(data.imports))
  await add('project/exports.json', jsonBlob(data.exports))
  await add('project/audit-events.json', jsonBlob(data.auditEvents))
  await add('project/presentations.json', jsonBlob(data.presentations))
  for (const file of files) await add(file.path, file.blob)

  const manifest: ProjectArchiveManifest = {
    schema: PROJECT_ARCHIVE_SCHEMA,
    created_at: new Date().toISOString(),
    project: {
      id: data.project.id,
      code: data.project.code,
      name: data.project.name,
      version: data.project.version,
      as_of_date: data.project.as_of_date,
    },
    entries,
  }
  zip.file('manifest.json', JSON.stringify(manifest, null, 2))
  return { blob: await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } }), manifest }
}

async function collectFiles(data: Omit<ProjectArchiveData, 'project' | 'versions' | 'attachments' | 'imports' | 'exports' | 'auditEvents' | 'presentations'> & Pick<ProjectArchiveData, 'attachments' | 'imports' | 'presentations'>) {
  const files: ProjectArchiveFile[] = []
  for (const attachment of data.attachments) {
    files.push({
      path: `files/attachments/${attachment.id}-${safeName(attachment.filename)}`,
      blob: await downloadStorageBlob('suiviaziz-attachments', attachment.storage_path),
    })
  }
  for (const imported of data.imports) {
    files.push({
      path: `files/imports/${imported.id}-${safeName(imported.filename)}`,
      blob: await downloadStorageBlob('suiviaziz-imports', imported.storage_path),
    })
  }
  for (const edition of data.presentations) {
    files.push({
      path: `files/presentations/${edition.id}/presentation.pptx`,
      blob: await downloadStorageBlob('suiviaziz-presentations', edition.pptx_storage_path),
    })
    if (edition.pdf_storage_path) {
      files.push({
        path: `files/presentations/${edition.id}/presentation.pdf`,
        blob: await downloadStorageBlob('suiviaziz-presentations', edition.pdf_storage_path),
      })
    }
  }
  return files
}

export async function createProjectArchive(project: ProjectRow) {
  const [versions, attachments, imports, exports, auditEvents, presentations] = await Promise.all([
    listProjectVersions(project.id),
    listProjectAttachments(project.id),
    listProjectImports(project.id),
    listProjectExports(project.id),
    listProjectAuditEvents(project.id),
    listPresentationEditions(project.id),
  ])
  const data: ProjectArchiveData = { project, versions, attachments, imports, exports, auditEvents, presentations }
  const files = await collectFiles(data)
  return packageProjectArchive(data, files)
}

export async function readProjectArchive(file: Blob): Promise<LoadedProjectArchive> {
  const JSZip = await loadZipLibrary()
  let zip: JSZip
  try {
    zip = await JSZip.loadAsync(file)
  } catch {
    throw new Error('Ce fichier n’est pas une archive Suivi Aziz lisible.')
  }

  const manifestEntry = zip.file('manifest.json')
  if (!manifestEntry) throw new Error('Le manifeste de l’archive est absent.')
  const manifest = readJson<ProjectArchiveManifest>(await manifestEntry.async('text'), 'Le manifeste de l’archive')
  if (manifest.schema !== PROJECT_ARCHIVE_SCHEMA || !Array.isArray(manifest.entries)) {
    throw new Error('Cette archive n’est pas compatible avec Suivi Aziz.')
  }

  const files = new Map<string, Blob>()
  for (const entry of manifest.entries) {
    if (!entry.path || !/^[a-zA-Z0-9._/-]+$/.test(entry.path) || !/^[0-9a-f]{64}$/.test(entry.sha256) || !Number.isFinite(entry.bytes)) {
      throw new Error('Le manifeste de l’archive est invalide.')
    }
    const zipEntry = zip.file(entry.path)
    if (!zipEntry) throw new Error(`Fichier manquant dans l’archive : ${entry.path}`)
    const blob = await zipEntry.async('blob')
    if (blob.size !== entry.bytes || await sha256(blob) !== entry.sha256) {
      throw new Error(`Le contrôle d’intégrité a échoué : ${entry.path}`)
    }
    files.set(entry.path, blob)
  }

  const required = (path: string) => {
    const entry = files.get(path)
    if (!entry) throw new Error(`Élément obligatoire absent : ${path}`)
    return entry
  }
  const data: ProjectArchiveData = {
    project: readJson<ProjectRow>(await required('project/project.json').text(), 'Le projet'),
    versions: readJson<ProjectVersionRow[]>(await required('project/versions.json').text(), 'Les versions'),
    attachments: readJson<ProjectAttachment[]>(await required('project/attachments.json').text(), 'Les pièces jointes'),
    imports: readJson<ImportRow[]>(await required('project/imports.json').text(), 'Les imports'),
    exports: readJson<ExportRow[]>(await required('project/exports.json').text(), 'Les exports'),
    auditEvents: readJson<AuditEventRow[]>(await required('project/audit-events.json').text(), 'Le journal'),
    presentations: readJson<PresentationEditionRow[]>(await required('project/presentations.json').text(), 'Les présentations'),
  }
  assertProjectArchiveData(data)
  if (manifest.project.id !== data.project.id || manifest.project.code !== data.project.code) {
    throw new Error('Le manifeste ne correspond pas au projet archivé.')
  }
  return { manifest, data, files }
}

function restoreIdentity(project: ProjectRow) {
  const stamp = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(2, 14)
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase()
  const code = `PRJ-R-${stamp}-${suffix}`
  return {
    code,
    slug: `restauration-${project.slug}-${stamp}-${suffix.toLowerCase()}`.slice(0, 80).replace(/-+$/g, ''),
    name: `${project.name} — restauration`,
  }
}

export async function restoreProjectArchive(file: Blob): Promise<{ project: ProjectRow; restoredAttachments: number }> {
  const archive = await readProjectArchive(file)
  const identity = restoreIdentity(archive.data.project)
  const snapshot = structuredClone(archive.data.project.snapshot)
  snapshot.project.id = identity.code
  snapshot.project.name = identity.name
  snapshot.project.events.unshift({
    id: crypto.randomUUID(),
    date: snapshot.as_of_date,
    type: 'restauration_archive',
    summary: `Projet restauré depuis l’archive du ${new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(archive.manifest.created_at))}`,
  })

  let restored = await createProject({
    slug: identity.slug,
    code: identity.code,
    name: identity.name,
    scenario: 'Restauration d’archive',
    city: archive.data.project.city,
  }, snapshot)
  let restoredAttachments = 0

  for (const attachment of archive.data.attachments) {
    const path = `files/attachments/${attachment.id}-${safeName(attachment.filename)}`
    const blob = archive.files.get(path)
    if (!blob) throw new Error(`Pièce jointe absente de l’archive : ${attachment.filename}`)
    const step = allSteps(restored.snapshot).find((item) => item.id === attachment.step_id)
    const restoredFile = new File([blob], attachment.filename, { type: attachment.mime_type || blob.type || 'application/octet-stream' })
    const result = await uploadProjectAttachment({
      project: restored,
      stepId: step?.id,
      stepLabel: step?.name,
      file: restoredFile,
      metadata: {
        title: attachment.title,
        category: attachment.category,
        document_date: attachment.document_date,
        received_date: attachment.received_date,
        issuer: attachment.issuer,
        note: attachment.note,
      },
    })
    restored = result.project
    restoredAttachments += 1
  }

  return { project: restored, restoredAttachments }
}

export function downloadArchiveBlob(blob: Blob, project: Pick<ProjectRow, 'code' | 'name'>) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${safeName(project.code)}_${safeName(project.name)}_archive.zip`
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
